'use client'

import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import React, { useCallback, useEffect, useId, useRef, useState, type ChangeEvent } from 'react'

import { UseAutoCompleteAddress } from '@/hooks/useAutoCompleteAddress'

import { cn } from './ui/cn'

interface AddressAutoCompleteInputProps {
  label: string
  placeholder?: string
  autoComplete: UseAutoCompleteAddress
  className?: string
  labelClassName?: string
  inputClassName?: string
  onSelect?: (item: AutoCompleteItem) => void
}

/**
 * Ship&Play autocomplete field — controlled text input with an anchored
 * suggestion dropdown. Wraps a useAutoCompleteAddress() instance so the parent
 * (ShippingWidget) owns the origin/destination state independently.
 *
 * Selecting an item sets the displayed value to the item's formatted address
 * (with `<br>` tags stripped) and pushes the selection into the hook so the
 * parent can read `address` + `recognizedPlace` for submit.
 */
export const AddressAutoCompleteInput = ({
  label,
  placeholder,
  autoComplete,
  className,
  labelClassName,
  inputClassName,
  onSelect,
}: AddressAutoCompleteInputProps): React.ReactElement => {
  const [value, setValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastSelectedRef = useRef<string | null>(null)
  const generatedId = useId()
  const inputId = `${generatedId}-input`
  const listboxId = `${generatedId}-list`

  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const text = event.target.value
    setValue(text)
    // iOS Safari re-fires an input event with the committed value when a
    // controlled input is replaced while autocorrect marked text is active —
    // don't re-search or reopen for the selection that was just made.
    if (text === lastSelectedRef.current) return
    autoComplete.onInputSearchTerm(text)
    setIsOpen(text.trim().length > 0)
  }

  const handleSelect = useCallback(
    (item: AutoCompleteItem): void => {
      const rawFormatted = item.formatted_address ?? item.name ?? ''
      const display = rawFormatted.replaceAll('<br>', ', ')
      lastSelectedRef.current = display
      setValue(display)
      autoComplete.onSelectAutoCompleteItem(item)
      onSelect?.(item)
      setIsOpen(false)
    },
    [autoComplete, onSelect],
  )

  const { autoCompleteItems, isLoadingAutoComplete } = autoComplete
  const showDropdown = isOpen && value.trim().length > 0
  const showEmptyMessage = showDropdown && !isLoadingAutoComplete && autoCompleteItems.length === 0

  return (
    // A <div>, not a <label>, must wrap the dropdown: iOS Safari forwards taps
    // inside a label to its control, refocusing the input after a suggestion
    // tap and reopening the list.
    <div
      ref={containerRef}
      className={cn('snplay:group snplay:relative', 'snplay:block', className)}
    >
      <label
        htmlFor={inputId}
        className={cn(
          'snplay:mb-1 snplay:block snplay:text-sm snplay:font-medium snplay:transition-colors',
          'snplay:group-focus-within:text-primaryDark',
          labelClassName,
        )}
      >
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={showDropdown}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => {
          if (value.trim().length > 0 && value !== lastSelectedRef.current) setIsOpen(true)
        }}
        autoComplete="off"
        className={cn(
          'snplay:w-full snplay:rounded-sm snplay:bg-transparent',
          'snplay:text-lg snplay:outline-none',
          'snplay:focus-visible:outline-2 snplay:focus-visible:outline-offset-2',
          'snplay:focus-visible:outline-primaryDark',
          inputClassName,
        )}
      />
      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          className={cn(
            'snplay:absolute snplay:left-0 snplay:right-0 snplay:top-full',
            'snplay:z-50 snplay:mt-2 snplay:max-h-80 snplay:overflow-y-auto',
            'snplay:rounded-2xl snplay:border snplay:border-ui-decoratorLight snplay:bg-background',
            'snplay:py-2 snplay:text-left snplay:shadow-lg',
          )}
        >
          {isLoadingAutoComplete && (
            <li
              role="presentation"
              className="snplay:px-4 snplay:py-3 snplay:text-sm snplay:text-ui-secondary"
            >
              Searching…
            </li>
          )}
          {showEmptyMessage && (
            <li
              role="presentation"
              className="snplay:px-4 snplay:py-3 snplay:text-sm snplay:text-ui-secondary"
            >
              No matches
            </li>
          )}
          {!isLoadingAutoComplete &&
            autoCompleteItems.map((item, i) => {
              const formatted = (item.formatted_address ?? '').replaceAll('<br>', ', ')
              return (
                <li key={item.id ?? `${item.name}-${i}`} role="option" aria-selected="false">
                  <button
                    type="button"
                    // Commit on mousedown with preventDefault (as the shared
                    // Headless UI AutoCompleteInput does): the trailing click
                    // after a mobile tap is unreliable once the keyboard
                    // dismisses, and preventDefault keeps focus in the input.
                    onMouseDown={(event) => {
                      event.preventDefault()
                      if (event.button === 0) handleSelect(item)
                    }}
                    // Keyboard activation (Enter/Space) produces a click with
                    // detail === 0 and no preceding mousedown.
                    onClick={(event) => {
                      if (event.detail === 0) handleSelect(item)
                    }}
                    className={cn(
                      'snplay-can-focus snplay:flex snplay:w-full snplay:items-center',
                      'snplay:gap-3 snplay:px-4 snplay:py-3 snplay:text-left',
                      'snplay:transition snplay:hover:bg-primaryLight',
                    )}
                  >
                    {item.icon_url && (
                      <img
                        src={item.icon_url}
                        alt=""
                        className="snplay:size-6 snplay:shrink-0"
                        aria-hidden="true"
                      />
                    )}
                    <div className="snplay:min-w-0 snplay:flex-1">
                      <div
                        className={cn(
                          'snplay:truncate snplay:text-base',
                          'snplay:font-medium snplay:text-ui-primary',
                        )}
                      >
                        {item.name}
                      </div>
                      {formatted && (
                        <div className="snplay:truncate snplay:text-sm snplay:text-ui-secondary">
                          {formatted}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              )
            })}
        </ul>
      )}
    </div>
  )
}
